import { Button, Divider, IconButton, Menu, MenuItem } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import cx from 'classnames';
import {
  add,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  startOfYear,
  endOfYear,
  format,
} from 'date-fns';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';
import { LiftStatusStorage } from 'Utils/Storage';

import classes from './PeriodSelector.module.scss';
import {
  EGlobalStateCases,
  useGlobalState,
} from 'Providers/GlobalStateProvider';
import { nl } from 'date-fns/locale';
import { useUser } from 'Providers/UserProvider';

const LabelType = {
  current: 0,
  previous: -1,
  currentView: 2,
};

type TimeUnit = 'month' | 'quarter' | 'year' | 'custom';
export interface TimeSelection {
  unit?: TimeUnit;
  period: number;
  label?: string;
}

export interface DateRange {
  from: Date;
  to: Date;
}

export type PeriodSelectorProps = {
  hideActual?: boolean;
  onDateChange: (dateRange: DateRange, isInitial?: boolean) => void;
  compact?: boolean;
  leadingItems?: React.ReactNode;
};

const defaultTimeSelection: TimeSelection = {
  unit: 'year',
  period: LiftStatusStorage?.period?.period ?? LabelType.current,
  label: '',
};

const getLabel = (
  selection: TimeSelection,
  intl: IntlShape
): string | undefined => {
  if (selection.period === LabelType.current) {
    if (selection.unit === 'month')
      return intl.formatMessage({ id: 'current.month' });
    if (selection.unit === 'quarter')
      return intl.formatMessage({ id: 'current.quarter' });
    if (selection.unit === 'year')
      return intl.formatMessage({ id: 'current.year' });
    return undefined;
  }

  if (selection.period === LabelType.previous) {
    if (selection.unit === 'month')
      return intl.formatMessage({ id: 'previous.month' });
    if (selection.unit === 'quarter')
      return intl.formatMessage({ id: 'previous.quarter' });
    if (selection.unit === 'year')
      return intl.formatMessage({ id: 'previous.year' });
    return undefined;
  }

  return undefined;
};

const getInitialTimeSelection = (): TimeSelection => {
  const stored = LiftStatusStorage.period;
  return stored && stored.label !== defaultTimeSelection.label
    ? stored
    : defaultTimeSelection;
};

const getDateRange = (
  selection: TimeSelection,
  today: Date
): DateRange | undefined => {
  if (selection.unit === 'month') {
    const targetDate = add(today, { months: selection.period });
    return { from: startOfMonth(targetDate), to: endOfMonth(targetDate) };
  }

  if (selection.unit === 'quarter') {
    const targetDate = add(today, { months: selection.period * 3 });
    return { from: startOfQuarter(targetDate), to: endOfQuarter(targetDate) };
  }

  if (selection.unit === 'year') {
    const targetDate = add(today, { years: selection.period });
    return { from: startOfYear(targetDate), to: endOfYear(targetDate) };
  }

  return undefined;
};

const PeriodSelector = ({
  onDateChange,
  leadingItems,
  ...props
}: PeriodSelectorProps) => {
  const intl = useIntl();
  const [globalState, globalDispatch] = useGlobalState();

  const user = useUser();

  const [anchorEl, setAnchorEl] = React.useState<
    (EventTarget & HTMLButtonElement) | null
  >(null);

  const isOpen = Boolean(anchorEl);

  const [userAbleToModifyTime, setUserAbleToModifyTime] = React.useState(true);

  const [timeSelection, setTimeSelection] = React.useState<TimeSelection>(
    getInitialTimeSelection
  );
  const [dateRange, setDateRange] = React.useState<
    { from: Date; to: Date } | undefined
  >(() => getDateRange(timeSelection, new Date()));

  const changeDate = React.useCallback(
    (range: DateRange) => {
      setDateRange(range);
      onDateChange(range);
    },
    [onDateChange]
  );

  const periodLabel = React.useMemo(
    () => getLabel(timeSelection, intl),
    [timeSelection, intl]
  );

  // Change from and to dates for a given selection, persist it, and notify the parent
  //
  const applyTimeSelection = React.useCallback(
    (selection: TimeSelection) => {
      setTimeSelection(selection);

      const range = getDateRange(selection, new Date());
      if (range) {
        changeDate(range);
      }

      LiftStatusStorage.period = {
        ...selection,
        label: getLabel(selection, intl),
      };
    },
    [changeDate, intl]
  );

  // Notify the parent of the initial date range once on mount
  // (timeSelection/dateRange are already correct via their state initializers)
  //
  React.useEffect(() => {
    if (dateRange) {
      onDateChange(dateRange, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changePeriod = React.useCallback(
    (direction: number) => {
      applyTimeSelection({
        ...timeSelection,
        period: (timeSelection?.period ?? 0) + direction,
      });
    },
    [timeSelection, applyTimeSelection]
  );

  const changeTimeFlowToCurrent = React.useCallback(() => {
    globalDispatch({
      type: EGlobalStateCases.switchViewToCurrent,
      payload: true,
    });

    closeMenu();
  }, [globalDispatch]);

  const changeTimeSelection = (unit: TimeUnit, offset: number) => {
    globalDispatch({
      type: EGlobalStateCases.switchViewToCurrent,
      payload: false,
    });

    applyTimeSelection({ unit, period: offset });
    closeMenu();
  };

  // handle user roles and it's affect on the time selection
  //
  React.useEffect(() => {
    // In case the user is a "Client light" or "Monteur" we want to show the current situation
    // and disable the ability to change dates.
    //
    if (user.isClientLight || user.isMechanic) {
      changeTimeFlowToCurrent();
      // Reason I made this a state obj is for the future adaptation of more user roles.
      // this will be read in the UI aspect to remove / show items.
      //
      setUserAbleToModifyTime(false);
    }
  }, [
    user.roles,
    user.isClientLight,
    user.isMechanic,
    changeTimeFlowToCurrent,
  ]);

  const closeMenu = () => setAnchorEl(null);

  return (
    <>
      <div className={classes.buttonGroup}>
        {!globalState.viewIsCurrent && (
          <IconButton onClick={() => changePeriod(-1)}>
            <ChevronLeftIcon />
          </IconButton>
        )}

        {userAbleToModifyTime && (
          <Button
            variant="outlined"
            className={cx(
              classes.rangeBtn,
              props.compact ? classes.compactRangeBtn : null
            )}
            onClick={(event) => setAnchorEl(event.currentTarget)}
          >
            {globalState.viewIsCurrent ? (
              <FormattedMessage id="current.situation" />
            ) : dateRange &&
              timeSelection &&
              ![LabelType.current, LabelType.previous].includes(
                LiftStatusStorage?.period?.period ?? 0
              ) ? (
              (() => {
                const start = format(
                  new Date(LiftStatusStorage.startDate ?? new Date()),
                  'MMM yyyy',
                  { locale: nl }
                );

                const end = format(
                  new Date(LiftStatusStorage.endDate ?? new Date()),
                  'MMM yyyy',
                  { locale: nl }
                );

                return timeSelection.unit === 'month'
                  ? start
                  : `${start} t/m ${end}`;
              })()
            ) : (
              <>{LiftStatusStorage?.period?.label ?? periodLabel}</>
            )}
          </Button>
        )}

        {!globalState.viewIsCurrent && timeSelection.period < 0 && (
          <IconButton onClick={() => changePeriod(1)}>
            <ChevronRightIcon />
          </IconButton>
        )}
      </div>

      {userAbleToModifyTime && (
        <Menu
          id="period-selection-menu"
          anchorEl={anchorEl}
          keepMounted
          open={isOpen}
          onClose={closeMenu}
        >
          {leadingItems}

          {!props.hideActual && (
            <>
              <MenuItem onClick={() => changeTimeFlowToCurrent()}>
                <FormattedMessage id="current.situation" />
              </MenuItem>

              <Divider />
            </>
          )}

          <MenuItem
            onClick={() => changeTimeSelection('month', LabelType.current)}
          >
            <FormattedMessage id="current.month" />
          </MenuItem>

          <MenuItem
            onClick={() => changeTimeSelection('month', LabelType.previous)}
          >
            <FormattedMessage id="previous.month" />
          </MenuItem>
          <MenuItem
            onClick={() => changeTimeSelection('quarter', LabelType.current)}
          >
            <FormattedMessage id="current.quarter" />
          </MenuItem>
          <MenuItem
            onClick={() => changeTimeSelection('quarter', LabelType.previous)}
          >
            <FormattedMessage id="previous.quarter" />
          </MenuItem>
          <MenuItem
            onClick={() => changeTimeSelection('year', LabelType.current)}
          >
            <FormattedMessage id="current.year" />
          </MenuItem>
          <MenuItem
            onClick={() => changeTimeSelection('year', LabelType.previous)}
          >
            <FormattedMessage id="previous.year" />
          </MenuItem>
        </Menu>
      )}
    </>
  );
};

export default PeriodSelector;
